import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import { Split } from "@/api/accounting.tsx"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select"
import { LedgerAccounts } from "../accounts"
import {
  PromotionSelector,
  PromotionMatch,
  PromotionSearchContextSeed,
} from "@/accounting_detail/PromotionSelector.tsx"
import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { OrgDropdownCell } from "@/components/org_dropdown.tsx"
import { ComponentChildren } from "preact"
import { Info } from "lucide-react"
import { useState } from "preact/hooks"
import { execution_date_for_date_input } from "./execution_date_input"

const DATA_SPLIT_FORM = "data-accounting-split-form"
const CLEARABLE_SPLIT_FIELDS = ["retailer_name", "product_line", "product_category", "related_product_group", "chargeback_type"]

/** On Tab/Shift+Tab from a date input, move focus to next/prev form field to skip native date picker tab stops. */
function handleDateFieldKeyDown(e: KeyboardEvent) {
  if (e.key !== "Tab") return
  const form = (e.target as HTMLElement).closest(`[${DATA_SPLIT_FORM}]`)
  if (!form) return
  const focusables = Array.from(
    form.querySelectorAll<HTMLElement>('input:not([type="hidden"]), button, select, textarea')
  ).filter((el: HTMLElement) => el.getAttribute("tabindex") !== "-1")
  const current = document.activeElement as HTMLElement
  const index = focusables.indexOf(current)
  if (index === -1) return
  e.preventDefault()
  const nextIndex = (e as KeyboardEvent & { shiftKey: boolean }).shiftKey ? index - 1 : index + 1
  if (nextIndex < 0 || nextIndex >= focusables.length) return
  focusables[nextIndex].focus()
}

interface SplitFormFieldsProps {
  split: Partial<Split>
  onUpdate: (field: keyof Split, value: any) => void
  orgFieldConfigs?: OrgFieldConfig[]
  orgId?: string
  deductionId: string
  localAmount?: string
  onAmountChange?: (e: Event) => void
  onAmountBlur?: (e: Event) => void
  showOptionalFields?: boolean
  fieldIdPrefix?: string
  hideAmount?: boolean
  hideProductCategory?: boolean
  hideRetailerInvoice?: boolean
  betweenRequiredAndOptional?: ComponentChildren
  promotionSearchContextSeed?: PromotionSearchContextSeed
}

export function SplitFormFields({
  split,
  onUpdate,
  orgFieldConfigs = [],
  orgId,
  deductionId,
  localAmount,
  onAmountChange,
  onAmountBlur,
  showOptionalFields = true,
  fieldIdPrefix = "",
  hideAmount = false,
  hideProductCategory = false,
  hideRetailerInvoice = false,
  betweenRequiredAndOptional,
  promotionSearchContextSeed,
}: SplitFormFieldsProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const amountValue = localAmount !== undefined 
    ? localAmount 
    : (split.amount !== undefined && split.amount !== null 
      ? (typeof split.amount === 'number' ? split.amount.toFixed(2) : String(split.amount))
      : "")

  // Render org-specific fields (including product_category, retailer_invoice_number if configured)
  // Note: code_description is always shown above, so we exclude it from org fields
  function renderOrgFields() {
    if (!orgFieldConfigs || !orgId) return null

    const splitFormConfigs = orgFieldConfigs.filter(config => 
      config.is_active && 
      config.table_contexts.includes("split_form") &&
      config.field_name !== "code_description" // Exclude code_description since it's always shown
    )

    if (splitFormConfigs.length === 0) return null

    // Group fields for grid layout - most fields in 2-column grid
    const gridFields: typeof splitFormConfigs = []
    
    splitFormConfigs.forEach(config => {
      gridFields.push(config)
    })

    return (
      <>
        {/* Grid fields (2 columns) */}
        {gridFields.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gridFields.map(config => {
              const value = (split[config.field_name as keyof Split] as string) || ""
              const fieldId = `${fieldIdPrefix}${config.field_name}-${split.id || "new"}`
              const isClearableField = CLEARABLE_SPLIT_FIELDS.includes(config.field_name)
              
              if (config.field_type === "dropdown") {
                return (
                  <div key={config.id} className="space-y-1">
                    <Label htmlFor={fieldId} className="text-sm">
                      {config.display_name}
                    </Label>
                    <OrgDropdownCell
                      fieldName={config.field_name}
                      value={value}
                      orgId={orgId}
                      onUpdate={(newValue) => onUpdate(config.field_name as keyof Split, newValue)}
                      placeholder={`Select ${config.display_name.toLowerCase()}...`}
                      className="w-full"
                      allowClear={isClearableField}
                    />
                  </div>
                )
              }
              
              if (config.field_type === "date") {
                return (
                  <div key={config.id} className="space-y-1">
                    <Label htmlFor={fieldId} className="text-sm">
                      {config.display_name}
                    </Label>
                    <Input
                      id={fieldId}
                      type="date"
                      value={value}
                      onChange={(e: Event) => {
                        const target = e.target as HTMLInputElement
                        onUpdate(config.field_name as keyof Split, target.value)
                      }}
                      onKeyDown={handleDateFieldKeyDown}
                      placeholder={`Select ${config.display_name.toLowerCase()}...`}
                      className="w-full"
                    />
                    {isClearableField && value && (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-xs text-gray-500 hover:text-gray-700 justify-start"
                        onClick={() => onUpdate(config.field_name as keyof Split, "")}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                )
              }
              
              if (config.field_type === "text" || config.field_type === "number") {
                return (
                  <div key={config.id} className="space-y-1">
                    <Label htmlFor={fieldId} className="text-sm">
                      {config.display_name}
                    </Label>
                    <Input
                      id={fieldId}
                      type={config.field_type === "number" ? "number" : "text"}
                      value={value}
                      onChange={(e: Event) => {
                        const target = e.target as HTMLInputElement
                        onUpdate(config.field_name as keyof Split, target.value)
                      }}
                      placeholder={`Enter ${config.display_name.toLowerCase()}...`}
                      className="h-9"
                    />
                    {isClearableField && value && (
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-xs text-gray-500 hover:text-gray-700 justify-start"
                        onClick={() => onUpdate(config.field_name as keyof Split, "")}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                )
              }
              
              return null
            })}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {/* Required Fields */}
      <div className={`grid ${hideAmount ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4 mb-4`}>
        {/* Amount */}
        {!hideAmount && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Label htmlFor={`${fieldIdPrefix}amount-${split.id || "new"}`} className="text-sm font-medium">
                Amount *
              </Label>
              <TooltipProvider>
                <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      tabIndex={-1}
                      variant="ghost"
                      className="h-4 w-4 p-0 text-gray-400 hover:text-gray-600"
                      onMouseEnter={() => setTooltipOpen(true)}
                      onMouseLeave={() => setTooltipOpen(false)}
                      onFocus={() => setTooltipOpen(true)}
                      onBlur={() => setTooltipOpen(false)}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Use numbers or expressions like 100+20-5. We evaluate on blur/Enter and save the result.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id={`${fieldIdPrefix}amount-${split.id || "new"}`}
              type="text"
              inputMode="decimal"
              value={amountValue}
              onChange={onAmountChange || ((e: Event) => {
                const target = e.target as HTMLInputElement
                if (target.value === "" || target.value === null) {
                  onUpdate("amount", undefined)
                } else {
                  const num = parseFloat(target.value)
                  onUpdate("amount", isNaN(num) ? undefined : num)
                }
              })}
              onBlur={onAmountBlur || ((e: Event) => {
                const target = e.target as HTMLInputElement
                if (target.value === "" || target.value === null) {
                  onUpdate("amount", undefined)
                } else {
                  const num = parseFloat(target.value)
                  onUpdate("amount", isNaN(num) ? undefined : num)
                }
              })}
              placeholder="0.00"
              className="text-right h-9"
            />
          </div>
        )}

        {/* Account Code */}
        <div className="space-y-1">
          <Label htmlFor={`${fieldIdPrefix}account-${split.id || "new"}`} className="text-sm font-medium">Account Code *</Label>
          <AutocompleteSelect
            onSelect={(code: SelectItem) => {
              onUpdate("account_id", code.id)
              onUpdate("name", code.expense_account || code.name)
              onUpdate("expense_account", code.expense_account)
              onUpdate("product_service", code.product_service)
            }}
            initial={
              split.account_id
                ? {id: split.account_id, name: split.expense_account || split.name || "", account_number: split.account_number, product_service: split.product_service}
                : undefined
            }
            values={LedgerAccounts.get()}
          />
        </div>
      </div>

      {/* Slot for content between required and optional fields (e.g., toggle button) */}
      {betweenRequiredAndOptional}

      {/* Optional Fields */}
      {showOptionalFields && (
        <div className="mt-3 space-y-4">
          {/* Promotion Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor={`${fieldIdPrefix}promotion-${split.id || "new"}`} className="text-sm">Promotion</Label>
              <PromotionSelector
                deductionId={deductionId}
                onSelect={(match: PromotionMatch) => {
                  onUpdate("promotion_id", match.promotion.id)
                  onUpdate("promotion_name", match.promotion.name)
                  onUpdate("promotion_type", match.promotion.promotion_type)
                  onUpdate("promotion_discount_percentage", match.promotion.discount_percentage)
                  onUpdate("promotion_discount_amount", match.promotion.discount_amount)
                }}
                selectedPromotionId={split.promotion_id}
                className="h-9"
                searchContextSeed={
                  // The split's own execution date (when set and valid) takes precedence over the
                  // transaction-level date so the as-of filter matches the right promotion window
                  execution_date_for_date_input(split.execution_date)
                    ? {
                        ...promotionSearchContextSeed,
                        execution_date: execution_date_for_date_input(split.execution_date),
                      }
                    : promotionSearchContextSeed
                }
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${fieldIdPrefix}execution-date-${split.id || "new"}`} className="text-sm">Execution Date</Label>
              <Input
                id={`${fieldIdPrefix}execution-date-${split.id || "new"}`}
                type="date"
                value={execution_date_for_date_input(split.execution_date)}
                onChange={(e: Event) => {
                  const target = e.target as HTMLInputElement
                  onUpdate("execution_date", target.value)
                }}
                onKeyDown={handleDateFieldKeyDown}
                className="h-9"
              />
            </div>
          </div>

          {/* Promo Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor={`${fieldIdPrefix}promo-type-${split.id || "new"}`} className="text-sm">Promo Type</Label>
              <Input
                id={`${fieldIdPrefix}promo-type-${split.id || "new"}`}
                value={split.promo_type || ""}
                onChange={(e: Event) => {
                  const target = e.target as HTMLInputElement
                  onUpdate("promo_type", target.value)
                }}
                placeholder="e.g., TPR, Display"
                className="h-9"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor={`${fieldIdPrefix}promo-sub-type-${split.id || "new"}`} className="text-sm">Promo Sub Type</Label>
              <Input
                id={`${fieldIdPrefix}promo-sub-type-${split.id || "new"}`}
                value={split.promo_sub_type || ""}
                onChange={(e: Event) => {
                  const target = e.target as HTMLInputElement
                  onUpdate("promo_sub_type", target.value)
                }}
                placeholder="e.g., Floor Display, End Cap"
                className="h-9"
              />
            </div>
          </div>

          {/* Description - always visible by default */}
          <div className="space-y-1">
            <Label htmlFor={`${fieldIdPrefix}description-${split.id || "new"}`} className="text-sm">Description</Label>
            <Input
              id={`${fieldIdPrefix}description-${split.id || "new"}`}
              value={split.code_description || ""}
              onChange={(e: Event) => {
                const target = e.target as HTMLInputElement
                onUpdate("code_description", target.value)
              }}
              placeholder="Enter description..."
              className="h-9"
            />
          </div>

          {/* Organization Fields - includes product_category, retailer_invoice_number if configured (code_description excluded since it's always shown above) */}
          {renderOrgFields()}
        </div>
      )}
    </>
  )
}

